// ABOUTME: Zod path contract and types for the TechLog → Skylegs post-flight push.
import {
  ZSkylegsCategory,
  ZSkylegsFlight,
  ZSkylegsFlightPostFlightInput,
  ZSkylegsUser,
} from '../../../../activities/api/skylegs/skylegs.zod';
import { ZTechlogAircraftWithType } from '../../../../types/techLog/aircraft.zod';
import { ZTechlogDeiceEvent } from '../../../../types/techLog/deice.zod';
import {
  ZFlightFuelSummaryResponse,
  ZTechlogFluidUplift,
  ZTechlogFuelUplifts,
} from '../../../../types/techLog/fuel.zod';
import { ZTechLogSector } from '../../../../types/techLog/sector.zod';
import { ZTechLogTrip } from '../../../../types/techLog/trip.zod';
import { ZTechlogUser } from '../../../../types/techLog/user.zod';
import { z } from 'zod';

/**
 * Input validation schema for the TechLog → Skylegs flight transformation.
 * Flat structure: techlog.objectName and skylegs.objectName.
 */
export const ZTechlogToSkylegsFlightInput = z.object({
  techlog: z.object({
    sector: ZTechLogSector,
    // Carries the trip reference Skylegs stores as its tech log page number.
    trip: ZTechLogTrip,
    // Carries `fuelUnit` and `oilUnit`, which decide whether a mass or oil
    // quantity can be expressed in the units Skylegs stores, and the engine
    // count that bounds the per-engine oil slots.
    aircraft: ZTechlogAircraftWithType,
    // Null when the sector has no fuel record; the fuel fields are then omitted
    // rather than the whole post-flight push failing.
    fuelSummary: ZFlightFuelSummaryResponse.nullable(),
    fuelUplifts: z.array(ZTechlogFuelUplifts),
    fluidUplifts: z.array(ZTechlogFluidUplift),
    deiceEvents: z.array(ZTechlogDeiceEvent),
    // Null when the sector records no pilot in that seat.
    pilotInCommand: ZTechlogUser.nullable(),
    secondInCommand: ZTechlogUser.nullable(),
  }),
  skylegs: z.object({
    // Every category on the tenant. Categories span several kinds and only
    // `kind: "MissionFlight"` rows are flight categories, so the resolution has
    // to see the kind alongside the name.
    categories: z.array(ZSkylegsCategory).min(1),
    // Organisation memberships, which carry the crew seat code and the email
    // TechLog pilots are matched on.
    organisationUsers: z.array(ZSkylegsUser),
    // The flight the push updates, as it stands now. `crew_string` is an ordered
    // positional list of the whole crew, so writing the flight deck without
    // reading the rest of that list first would drop everyone else on board.
    // Null when the path carries no destination flight to update.
    flight: ZSkylegsFlight.nullable(),
  }),
});

export const ZTechlogToSkylegsFlightOutput = ZSkylegsFlightPostFlightInput;

// Type exports - inferred from Zod schemas (single source of truth)
export type TechlogToSkylegsFlightInput = z.infer<
  typeof ZTechlogToSkylegsFlightInput
>;
export type TechlogToSkylegsFlightOutput = z.infer<
  typeof ZTechlogToSkylegsFlightOutput
>;
